import React, { useState, useEffect, useMemo, memo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  TooltipProps
} from "recharts";
import { UserData } from "@/entities/users/types";
import { DirectReportMonthlyData } from "@/entities/reporting/types";
import {
  NameType,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";
import { DirectReportCustomTooltip } from "@/components/graphs/WinRate/LeadAnalysisCustomTooltips";
import { ChartIcons } from "@/components/graphs/WinRate/ChartIcons";
import UserAvatar from "@/components/controls/UserAvatar";
import {
  ChartDataPoint,
  MonthlyLeadsChartProps,
  UserLine
} from "@/components/layouts/LeadAnalysis/types";
import { sortBy } from "lodash";
import { reducePrecision } from "@/utils/numbers";

const LINE_COLORS = [
  "#523C15",
  "#0B2985",
  "#2A5755",
  "#660A57",
  "#39318C",
  "#005782",
  "#24544F"
];

const DOT_COLORS = [
  "#EC9D16",
  "#3D7CF1",
  "#42B0A6",
  "#EE38D0",
  "#867CF8",
  "#33BBFF",
  "#00F5A3"
];

const generateIconsMap = (
  userMap: Map<string, UserData>,
  userLines: Array<{ userId: string; lineColor: string; dotColor: string }>
): Record<string, React.ReactNode> => {
  const iconsMap: Record<string, React.ReactNode> = {};
  userLines.forEach(({ userId }) => {
    const userData = userMap.get(userId);
    if (userData) {
      iconsMap[userId] = (
        <UserAvatar size="M" userData={userData} />
      );
    }
  });
  return iconsMap;
};

const MonthlyLeadsChart: React.FC<MonthlyLeadsChartProps> = ({
  data,
  userMap,
  height = 300,
  isPercentage = false
}) => {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [userLines, setUserLines] = useState<UserLine[]>([]);
  const icons = useMemo(
    () => generateIconsMap(userMap, userLines),
    [userMap, userLines]
  );

  useEffect(() => {
    if (!data || !data.byMonth) {
      return;
    }

    const transformedData = transformData(data);
    setChartData(transformedData.chartData);
    setUserLines(transformedData.userLines);
  }, [data, userMap]);

  const transformData = (data: DirectReportMonthlyData) => {
    if (!data || !data.byMonth) {
      return { chartData: [], userLines: [] };
    }

    const months = Object.keys(data.byMonth);

    const allUserIds = new Set<string>();
    months.forEach((month) => {
      Object.keys(data.byMonth[month]).forEach((userId) => {
        allUserIds.add(userId);
      });
    });

    const userIds = Array.from(allUserIds);

    const userLines: UserLine[] = userIds.map((userId, index) => {
      return {
        userId,
        lineColor: LINE_COLORS[index % LINE_COLORS.length],
        dotColor: DOT_COLORS[index % DOT_COLORS.length]
      };
    });

    const chartData: ChartDataPoint[] = months.map((month) => {
      // Format the date to show as MM/YY for display
      const dateParts = month.split("-");
      const formattedMonth = `${dateParts[1]}/${dateParts[0].substring(2)}`;

      const monthData: ChartDataPoint = {
        month: formattedMonth,
        originalMonth: month
      };

      userLines.forEach(({ userId }) => {
        monthData[userId] = reducePrecision(
          data.byMonth[month][userId] || 0,
          1
        );
      });

      return monthData;
    });

    const sortedChartData = sortBy(chartData, (item) => item.originalMonth);

    return { chartData: sortedChartData, userLines };
  };

  const MemoizedComparisonTooltip = useMemo(() => {
    const ComparisonTooltip = ({
      active,
      payload
    }: TooltipProps<ValueType, NameType>) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
          <DirectReportCustomTooltip
            data={data}
            userMap={userMap}
            isPercentage={isPercentage}
          />
        );
      }
      return null;
    };
    return memo(ComparisonTooltip);
  }, [chartData, isPercentage]);

  return (
    <div className="flex w-[97%] h-[300px]">
      <ResponsiveContainer width="95%" height={height}>
        <LineChart data={chartData} margin={{ left: 25, right: 25, top: 5 }}>
          <CartesianGrid
            strokeDasharray="5 5"
            stroke="var(--widget)"
            horizontal={false}
          />
          <XAxis
            dataKey="month"
            interval={0}
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={{ fill: "var(--secondary)", fontSize: 10 }}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: "var(--widget)" }}
            tick={{ fill: "var(--secondary)", fontSize: 12 }}
          />
          <RechartsTooltip
            content={<MemoizedComparisonTooltip />}
            cursor={{ fill: "transparent" }}
          />

          {userLines.map((user) => (
            <Line
              key={user.userId}
              type="linear"
              dataKey={user.userId}
              stroke={user.lineColor}
              strokeWidth={2}
              dot={{
                r: 4,
                fill: user.dotColor,
                strokeWidth: 0
              }}
              activeDot={{
                r: 6,
                fill: user.dotColor,
                strokeWidth: 0,
                className: "cursor-pointer"
              }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <ChartIcons
        chartData={chartData}
        height={height}
        icons={icons}
        showComparison={true}
        iconKeyFormatter={(key) => key}
      />
    </div>
  );
};

export default MonthlyLeadsChart;
